import React, { useEffect, useState } from "react";
import { Modal, Button, Form, ModalBody, ModalFooter, ModalHeader, Row, Col } from "reactstrap";
import CustomInput from "../common/CustomInput";
import CustomTextArea from "../common/CustomTextArea";
import SelectInput from "../common/SelectInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { CreateCoupon, UpdateCoupon, GETPRODUCTS } from "../../redux/actions/productActions";
import { RESET_ERROR } from "../../redux/constants/userConstants";
import moment from "moment";

const AddCoupon = ({ openModal, onCloseModal, data, setActiveData, title }) => {
    const dispatch = useDispatch();
    const { newcoupon, error, productlist } = useSelector(state => state.product);
    const [loading, setLoading] = useState(false);
    const [productArray, setProductArray] = useState([]);

    // Helper function to extract value from react-select object
    const extractValue = (value) => {
        // Handle null/undefined
        if (value === null || value === undefined || value === '') {
            return value;
        }

        // If it's already a string, clean it up
        if (typeof value === 'string') {
            // Handle string values that might be double-quoted or escaped
            let cleaned = value;
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                cleaned = cleaned.slice(1, -1);
            }
            // Remove any escape characters
            cleaned = cleaned.replace(/\\"/g, '"');
            return cleaned;
        }

        // If it's an object from react-select, extract the value
        if (typeof value === 'object' && value !== null) {
            if (value.value !== undefined) {
                const extracted = value.value;
                // Handle string values that might be double-quoted or escaped
                if (typeof extracted === 'string') {
                    let cleaned = extracted;
                    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                        cleaned = cleaned.slice(1, -1);
                    }
                    // Remove any escape characters
                    cleaned = cleaned.replace(/\\"/g, '"');
                    return cleaned;
                }
                return extracted;
            }
        }

        return value;
    };

    const schema = yup.object({
        coupon_code: yup.string()
            .required("Coupon code is required")
            .max(50, "Coupon code must be at most 50 characters"),
        discount_type: yup.string()
            .transform(extractValue)
            .required("Discount type is required")
            .oneOf(["percentage", "fixed"], "Discount type must be percentage or fixed"),
        discount_value: yup.number()
            .required("Discount value is required")
            .positive("Discount value must be positive")
            .typeError("Discount value must be a number"),
        start_date: yup.date()
            .required("Start date is required")
            .typeError("Start date is required"),
        end_date: yup.date()
            .required("End date is required")
            .min(yup.ref("start_date"), "End date must be after start date")
            .typeError("End date is required"),
        usage_limit_type: yup.string()
            .transform(extractValue)
            .required("Usage limit type is required")
            .oneOf(["overall", "per_user"], "Usage limit type must be overall or per_user"),
        usage_limit_value: yup.number()
            .required("Usage limit value is required")
            .positive("Usage limit value must be positive")
            .integer("Usage limit value must be an integer")
            .typeError("Usage limit value must be a number"),
        // coupon_type: yup.string()
        //     .transform(extractValue)
        //     .required("Coupon type is required")
        //     .oneOf(["products", "all"], "Coupon type must be products or all"),
        // coupon_products: yup.array()
        //     .when("coupon_type", {
        //         is: (val) => {
        //             const extracted = extractValue(val);
        //             return extracted === "products";
        //         },
        //         then: (schema) => schema
        //             .min(1, "At least one product must be selected")
        //             .required("Products are required when coupon type is products"),
        //         otherwise: (schema) => schema.nullable()
        //     }),
        min_cart_value: yup.number()
            .required("Min cart value is required")
            .min(0, "Min cart value must be 0 or greater")
            .typeError("Min cart value must be a number"),
        coupon_details: yup.string()
            .nullable()
            .max(1000, "Coupon details must be at most 1000 characters"),
        visibility: yup.string()
            .transform(extractValue)
            .required("Visibility is required")
            .oneOf(["public", "private"], "Visibility must be public or private")
    });

    const { control, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            coupon_code: '',
            discount_type: '',
            discount_value: '',
            start_date: '',
            end_date: '',
            usage_limit_type: '',
            usage_limit_value: '',
            // coupon_type: '',
            // coupon_products: [],
            min_cart_value: '',
            coupon_details: '',
            visibility: ''
        }
    });

    // const couponTypeValue = watch("coupon_type");
    // // Handle both object (from react-select) and string values
    // // react-select returns { value: "products", label: "Products" }
    // const couponType = typeof couponTypeValue === 'object' && couponTypeValue !== null 
    //     ? couponTypeValue.value 
    //     : couponTypeValue || '';

    // Define options before use in useEffect
    const discountTypeOptions = [
        { value: "percentage", label: "Percentage" },
        { value: "fixed", label: "Fixed" }
    ];

    const usageLimitTypeOptions = [
        { value: "overall", label: "Overall" },
        { value: "per_user", label: "Per User" }
    ];

    const couponTypeOptions = [
        { value: "products", label: "Products" },
        { value: "all", label: "All" }
    ];

    const visibilityOptions = [
        { value: "public", label: "Public" },
        { value: "private", label: "Private" }
    ];

    useEffect(() => {
        dispatch(GETPRODUCTS());
    }, []);

    useEffect(() => {
        if (productlist && Array.isArray(productlist) && productlist.length > 0) {
            let products = [];
            productlist.forEach((prod) => {
                if (prod?._id && prod?.name) {
                    products.push({
                        value: prod._id,
                        label: prod.name,
                    });
                }
            });
            setProductArray(products);
        } else {
            setProductArray([]);
        }
    }, [productlist]);

    useEffect(() => {
        if (data) {
            setValue("coupon_code", data?.coupon_code || '');

            // Set discount_type as object for react-select
            if (data?.discount_type) {
                const discountTypeOption = discountTypeOptions.find(opt => opt.value === data.discount_type);
                setValue("discount_type", discountTypeOption || { value: data.discount_type, label: data.discount_type });
            } else {
                setValue("discount_type", '');
            }

            setValue("discount_value", data?.discount_value || '');
            setValue("start_date", data?.start_date ? moment(data.start_date).format('YYYY-MM-DD') : '');
            setValue("end_date", data?.end_date ? moment(data.end_date).format('YYYY-MM-DD') : '');

            // Set usage_limit_type as object for react-select
            if (data?.usage_limit_type) {
                const usageLimitTypeOption = usageLimitTypeOptions.find(opt => opt.value === data.usage_limit_type);
                setValue("usage_limit_type", usageLimitTypeOption || { value: data.usage_limit_type, label: data.usage_limit_type });
            } else {
                setValue("usage_limit_type", '');
            }

            setValue("usage_limit_value", data?.usage_limit_value || '');

            // // Set coupon_type as object for react-select
            // if (data?.coupon_type) {
            //     const couponTypeOption = couponTypeOptions.find(opt => opt.value === data.coupon_type);
            //     setValue("coupon_type", couponTypeOption || { value: data.coupon_type, label: data.coupon_type });
            // } else {
            //     setValue("coupon_type", '');
            // }

            setValue("min_cart_value", data?.min_cart_value || '');
            setValue("coupon_details", data?.coupon_details || '');

            // Set visibility as object for react-select
            if (data?.visibility) {
                const visibilityOption = visibilityOptions.find(opt => opt.value === data.visibility);
                setValue("visibility", visibilityOption || { value: data.visibility, label: data.visibility });
            } else {
                setValue("visibility", '');
            }

            // // Handle coupon_products - only set if productArray is available
            // if (data?.coupon_products && Array.isArray(data.coupon_products) && data.coupon_products.length > 0 && productArray.length > 0) {
            //     const selectedProducts = data.coupon_products.map(prodId => {
            //         const product = productArray.find(p => p.value === prodId);
            //         return product || { value: prodId, label: prodId };
            //     }).filter(Boolean);
            //     setValue("coupon_products", selectedProducts);
            // } else {
            //     setValue("coupon_products", []);
            // }
        } else if (!data) {
            reset();
        }
    }, [data]);

    useEffect(() => {
        if (newcoupon) {
            reset();
        }
    }, [newcoupon]);

    useEffect(() => {
        if (error) {
            dispatch({ type: RESET_ERROR });
        }
    }, [error]);

    const saveCoupon = (formValues) => {
        setLoading(true);

        // Extract value from react-select object if needed
        const getValue = (val) => {
            if (typeof val === 'object' && val !== null && val.value) {
                return val.value;
            }
            return val;
        };

        // const couponTypeValue = getValue(formValues.coupon_type);
        const discountTypeValue = getValue(formValues.discount_type);
        const usageLimitTypeValue = getValue(formValues.usage_limit_type);
        const visibilityValue = getValue(formValues.visibility);

        const payload = {
            coupon_code: formValues.coupon_code,
            discount_type: discountTypeValue,
            discount_value: formValues.discount_value,
            start_date: moment(formValues.start_date).format('YYYY-MM-DD'),
            end_date: moment(formValues.end_date).format('YYYY-MM-DD'),
            usage_limit_type: usageLimitTypeValue,
            usage_limit_value: formValues.usage_limit_value,
            coupon_type: "all", // Always set to "all"
            min_cart_value: formValues.min_cart_value,
            coupon_details: formValues.coupon_details || '',
            visibility: visibilityValue,
            coupon_products: [] // Always empty since coupon_type is always "all"
        };

        // if (couponTypeValue === "products" && formValues.coupon_products) {
        //     payload.coupon_products = formValues.coupon_products.map(prod => {
        //         if (typeof prod === 'object' && prod !== null && prod.value) {
        //             return prod.value;
        //         }
        //         return prod;
        //     });
        // } else {
        //     payload.coupon_products = [];
        // }

        if (data?._id) {
            payload.id = data._id;
        }

        const action = data ? UpdateCoupon : CreateCoupon;
        dispatch(action(payload)).finally(() => setLoading(false));
    };

    const clearclose = () => {
        setActiveData(null);
        onCloseModal();
        reset();
    };

    return (
        <Modal isOpen={openModal} toggle={clearclose} size="lg">
            <ModalHeader toggle={clearclose}>
                <h5 className="modal-title f-w-600">
                    {title}
                </h5>
            </ModalHeader>
            <ModalBody>
                <Form>
                    <Row>
                        <Col md="6">
                            <CustomInput
                                fieldLabel='Coupon Code :'
                                control={control}
                                error={errors.coupon_code}
                                fieldName='coupon_code'
                                readOnly={!!data?._id}
                            />
                        </Col>
                        <Col md="6">
                            <SelectInput
                                fieldLabel='Discount Type :'
                                control={control}
                                error={errors.discount_type}
                                fieldName='discount_type'
                                options={discountTypeOptions}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <CustomInput
                                fieldLabel='Discount Value :'
                                control={control}
                                error={errors.discount_value}
                                fieldName='discount_value'
                                type="number"
                            />
                        </Col>
                        <Col md="6">
                            <CustomInput
                                fieldLabel='Start Date :'
                                control={control}
                                error={errors.start_date}
                                fieldName='start_date'
                                type="date"
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <CustomInput
                                fieldLabel='End Date :'
                                control={control}
                                error={errors.end_date}
                                fieldName='end_date'
                                type="date"
                            />
                        </Col>
                        <Col md="6">
                            <SelectInput
                                fieldLabel='Usage Limit Type :'
                                control={control}
                                error={errors.usage_limit_type}
                                fieldName='usage_limit_type'
                                options={usageLimitTypeOptions}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <CustomInput
                                fieldLabel='Usage Limit Value :'
                                control={control}
                                error={errors.usage_limit_value}
                                fieldName='usage_limit_value'
                                type="number"
                            />
                        </Col>
                        <Col md="6">
                            <CustomInput
                                fieldLabel='Min Cart Value :'
                                control={control}
                                error={errors.min_cart_value}
                                fieldName='min_cart_value'
                                type="number"
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <SelectInput
                                fieldLabel='Visibility :'
                                control={control}
                                error={errors.visibility}
                                fieldName='visibility'
                                options={visibilityOptions}
                            />
                        </Col>
                    </Row>
                    {/* {couponType === "products" && (
                        <Row>
                            <Col md="12">
                                <SelectInput
                                    fieldLabel='Choose Products :'
                                    control={control}
                                    error={errors.coupon_products}
                                    fieldName='coupon_products'
                                    options={productArray}
                                    isMultiple={true}
                                />
                            </Col>
                        </Row>
                    )} */}
                    <Row>
                        <Col md="12">
                            <CustomTextArea
                                fieldLabel='Coupon Details :'
                                control={control}
                                error={errors.coupon_details}
                                fieldName='coupon_details'
                                type="textarea"
                                placeholder="Enter coupon details..."
                            />
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button
                    type="button"
                    disabled={loading}
                    color="primary"
                    onClick={handleSubmit(saveCoupon)}
                >
                    {loading ? "Saving" : "Save"}
                </Button>
                <Button
                    type="button"
                    color="secondary"
                    onClick={clearclose}
                >
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AddCoupon;

